/**
 * Einkaufsliste, Vorrat und Haushalt — **lokal zuerst**, wie alles andere.
 *
 * Neu ist nur, wem die Zeilen gehören: dem Haushalt. Jede Zeile trägt deshalb
 * `householdId`; welcher das ist, hat der Server beim letzten Abgleich gesagt.
 * Vor dem allerersten Abgleich gibt es keinen — dann kann auch nichts auf die
 * Liste, und die Oberfläche sagt das, statt Zeilen ohne Haushalt anzulegen, die
 * der Server später ablehnen müsste.
 *
 * **Die Lesefunktionen bekommen den Haushalt hineingereicht**, statt ihn selbst
 * nachzuschlagen. Dexies Live-Abfrage verliert die Beobachtung, wenn vor der
 * eigentlichen Abfrage zwei Ebenen verschachtelter `await` liegen: Die Liste
 * zeigte einen frisch angelegten Eintrag erst nach dem nächsten Sync — offline
 * also nie. Gefunden in der laufenden App, nicht im Test.
 */

import {
  addDays,
  buildShoppingNeeds,
  consumptionSuggestions,
  householdNeeds,
  itemKey,
  rememberedShop,
  mergeIntoStock,
  newId,
  shelfOrder,
} from '@kraftwerk/shared';
import type {
  AmountUnit,
  ConsumptionSuggestion,
  IsoDate,
  NeedLine,
  ShelfCategory,
  ShoppingNeeds,
} from '@kraftwerk/shared';
import { apiFetch } from '../api.js';
import { db, readHouseholdIds } from './local.js';
import type {
  KraftwerkDatabase,
  LocalHouseholdMember,
  LocalPantryItem,
  LocalShoppingItem,
} from './local.js';
import { stamp, writeRow, writeRows } from './write.js';
import type { RowChange } from './write.js';

export class NoHouseholdError extends Error {
  constructor() {
    super('Noch kein Haushalt — erst einmal abgleichen.');
  }
}

/** Der Haushalt des Kontos laut letztem Abgleich. `null` vor dem ersten. */
export async function currentHouseholdId(database: KraftwerkDatabase = db): Promise<string | null> {
  const ids = await readHouseholdIds(database);
  return ids?.[0] ?? null;
}

async function requireHousehold(database: KraftwerkDatabase): Promise<string> {
  const id = await currentHouseholdId(database);
  if (!id) throw new NoHouseholdError();
  return id;
}

function byShelf<T extends { category: ShelfCategory; name: string }>(a: T, b: T): number {
  return shelfOrder(a.category) - shelfOrder(b.category) || a.name.localeCompare(b.name, 'de');
}

// --- Einkaufsliste -------------------------------------------------------------

export async function listShopping(
  householdId: string | null,
  database: KraftwerkDatabase = db,
): Promise<LocalShoppingItem[]> {
  if (!householdId) return [];
  const rows = await database.shopping_items.where('householdId').equals(householdId).toArray();
  return rows.filter((row) => !row.deletedAt).sort(byShelf);
}

export interface ItemDraft {
  name: string;
  amount?: number | null;
  unit?: AmountUnit | null;
  category?: ShelfCategory;
  note?: string | null;
  /** Laden (18.10.2026); fehlt/`null` = egal. */
  shopId?: string | null;
}

function shoppingRow(householdId: string, draft: ItemDraft, fromPlan: boolean): LocalShoppingItem {
  return {
    id: newId(),
    updatedAt: stamp(),
    deletedAt: null,
    householdId,
    name: draft.name.trim(),
    // Keine Menge ist keine Menge — nicht 0.
    amount: draft.amount ?? null,
    unit: draft.amount !== null && draft.amount !== undefined ? (draft.unit ?? null) : null,
    category: draft.category ?? 'other',
    note: draft.note ?? null,
    fromPlan,
    checkedAt: null,
    shopId: draft.shopId ?? null,
  };
}

export async function addShoppingItem(
  draft: ItemDraft,
  database: KraftwerkDatabase = db,
): Promise<LocalShoppingItem> {
  const householdId = await requireHousehold(database);
  return writeRow('shopping_items', shoppingRow(householdId, draft, false), 'upsert', database);
}

export async function setChecked(
  id: string,
  checked: boolean,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const existing = await database.shopping_items.get(id);
  if (!existing || existing.deletedAt) return;
  const now = stamp();
  await writeRow(
    'shopping_items',
    { ...existing, checkedAt: checked ? now : null, updatedAt: now },
    'upsert',
    database,
  );
}

export async function removeShoppingItem(
  id: string,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const existing = await database.shopping_items.get(id);
  if (!existing || existing.deletedAt) return;
  const now = stamp();
  await writeRow(
    'shopping_items',
    { ...existing, deletedAt: now, updatedAt: now },
    'delete',
    database,
  );
}

/**
 * Gelöscht und gleich zurückgenommen (Wischen, 24.10.2026): dieselbe Zeile
 * wieder lebendig — mit neuem Zeitstempel, damit es jedes Gerät erfährt.
 */
export async function restoreShoppingItem(
  item: LocalShoppingItem,
  database: KraftwerkDatabase = db,
): Promise<void> {
  await writeRow(
    'shopping_items',
    { ...item, deletedAt: null, updatedAt: stamp() },
    'upsert',
    database,
  );
}

/**
 * Einkauf abschließen: Abgehaktes wandert in den Vorrat und von der Liste.
 *
 * Gleiche Sache, gleiche Einheit → die Menge wird addiert. Sonst entsteht ein
 * eigener Vorratseintrag, statt Gramm und Stück zu einer Zahl zu verrühren.
 * Alles in **einer** Transaktion: halb in den Vorrat gebucht und halb noch auf
 * der Liste wäre doppelt gezählt.
 */
export async function finishShopping(database: KraftwerkDatabase = db): Promise<number> {
  const householdId = await requireHousehold(database);
  const checked = (await listShopping(householdId, database)).filter((item) => item.checkedAt);
  if (checked.length === 0) return 0;

  const now = stamp();
  // Arbeitskopie des Vorrats: Zwei abgehakte Packungen Tofu landen in
  // derselben Vorratszeile, nicht in zwei.
  const stock = new Map(
    (await listPantry(householdId, database)).map((entry) => [entry.id, entry]),
  );
  const changed = new Set<string>();
  const changes: RowChange[] = [];

  for (const item of checked) {
    const match = [...stock.values()].find((entry) => mergeIntoStock(entry, item) !== null);
    const merged = match ? mergeIntoStock(match, item) : null;

    if (match && merged) {
      stock.set(match.id, { ...match, ...merged, updatedAt: now });
      changed.add(match.id);
    } else if (
      item.amount === null &&
      [...stock.values()].some((entry) => itemKey(entry.name) === itemKey(item.name))
    ) {
      // Ohne Menge gekauft, und es steht schon im Vorrat: Ein zweiter Eintrag
      // „Reis, Menge unbekannt" sagte nichts Neues.
    } else {
      const fresh: LocalPantryItem = {
        id: newId(),
        updatedAt: now,
        deletedAt: null,
        householdId,
        name: item.name,
        amount: item.amount,
        unit: item.unit,
        category: item.category,
        note: null,
      };
      stock.set(fresh.id, fresh);
      changed.add(fresh.id);
    }

    changes.push({
      table: 'shopping_items',
      row: { ...item, deletedAt: now, updatedAt: now },
      kind: 'delete',
    });
  }

  for (const id of changed) {
    const row = stock.get(id);
    if (row) changes.push({ table: 'pantry_items', row, kind: 'upsert' });
  }

  await writeRows(changes, database);
  return checked.length;
}

// --- Vorrat --------------------------------------------------------------------

export async function listPantry(
  householdId: string | null,
  database: KraftwerkDatabase = db,
): Promise<LocalPantryItem[]> {
  if (!householdId) return [];
  const rows = await database.pantry_items.where('householdId').equals(householdId).toArray();
  return rows.filter((row) => !row.deletedAt).sort(byShelf);
}

export async function addPantryItem(
  draft: ItemDraft,
  database: KraftwerkDatabase = db,
): Promise<LocalPantryItem> {
  const householdId = await requireHousehold(database);
  const row: LocalPantryItem = {
    id: newId(),
    updatedAt: stamp(),
    deletedAt: null,
    householdId,
    name: draft.name.trim(),
    amount: draft.amount ?? null,
    unit: draft.amount !== null && draft.amount !== undefined ? (draft.unit ?? null) : null,
    category: draft.category ?? 'other',
    note: draft.note ?? null,
  };
  return writeRow('pantry_items', row, 'upsert', database);
}

/** Menge setzen — `null` heißt „vorhanden, Menge unbekannt". */
export async function setPantryAmount(
  id: string,
  amount: number | null,
  unit: AmountUnit | null,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const existing = await database.pantry_items.get(id);
  if (!existing || existing.deletedAt) return;
  await writeRow(
    'pantry_items',
    { ...existing, amount, unit: amount === null ? null : unit, updatedAt: stamp() },
    'upsert',
    database,
  );
}

export async function removePantryItem(
  id: string,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const existing = await database.pantry_items.get(id);
  if (!existing || existing.deletedAt) return;
  const now = stamp();
  await writeRow(
    'pantry_items',
    { ...existing, deletedAt: now, updatedAt: now },
    'delete',
    database,
  );
}

// --- Aus dem Wochenplan --------------------------------------------------------

/**
 * Was der Plan von `from` bis einschließlich `to` braucht — minus Vorrat und
 * minus dem, was schon offen auf der Liste steht.
 *
 * Zwei Pläne zählen (27.10.2026): der **eigene** (wie bisher — jedes Mitglied
 * übernimmt seinen auf die gemeinsame Liste) und der **des Haushalts**, dort
 * mit den Portionen aller Mitesser plus Extras und Varianten. Liste und
 * Vorrat gehören dem Haushalt.
 */
export async function shoppingNeeds(
  from: string,
  to: string,
  householdId: string | null,
  database: KraftwerkDatabase = db,
): Promise<ShoppingNeeds> {
  const plan = (
    await database.meal_plan_entries.where('date').between(from, to, true, true).toArray()
  ).filter((row) => !row.deletedAt);

  const recipes = (await database.recipes.toArray()).filter((row) => !row.deletedAt);
  const ingredients = (await database.recipe_ingredients.toArray()).filter((row) => !row.deletedAt);
  const foods = (await database.foods.toArray()).filter((row) => !row.deletedAt);
  // Direkt abgefragt, nicht über `listShopping`: jede zusätzliche Ebene ist hier
  // eine Gelegenheit für die Live-Abfrage, die Beobachtung zu verlieren.
  const shared = householdId
    ? {
        list: await database.shopping_items.where('householdId').equals(householdId).toArray(),
        pantry: await database.pantry_items.where('householdId').equals(householdId).toArray(),
      }
    : { list: [], pantry: [] };
  const onList = shared.list.filter((item) => !item.deletedAt && !item.checkedAt);
  const pantry = shared.pantry.filter((item) => !item.deletedAt);
  // Haushalts-Essensplan (27.10.2026): Zutaten × Portionen aller Mitesser,
  // dazu Extras und Varianten — ebenso direkt abgefragt.
  const meals = householdId
    ? {
        entries: await database.household_meals.where('householdId').equals(householdId).toArray(),
        eaters: await database.household_meal_eaters
          .where('householdId')
          .equals(householdId)
          .toArray(),
        members: await database.household_members
          .where('householdId')
          .equals(householdId)
          .toArray(),
      }
    : null;
  const household = meals
    ? householdNeeds(
        meals.entries
          .filter(
            (entry) =>
              !entry.deletedAt &&
              entry.status === 'planned' &&
              entry.date !== null &&
              entry.date >= from &&
              entry.date <= to,
          )
          .map((entry) => ({ id: entry.id, date: entry.date!, dish: entry.dish })),
        meals.members.filter((member) => !member.deletedAt).map((member) => member.userId),
        meals.eaters,
      )
    : undefined;

  return buildShoppingNeeds({
    household,
    plan,
    recipes,
    ingredients,
    foods: foods.map((food) => ({
      id: food.id,
      name: food.name,
      category: food.category ?? 'other',
      portionGrams: food.portionGrams,
    })),
    pantry,
    onList,
  });
}

/**
 * Was **ein** Gericht braucht — für „Fehlendes auf die Einkaufsliste" auf der
 * Gericht-Seite. Dieselbe Rechnung wie für den Wochenplan, nur mit einem
 * einzigen gedachten Planeintrag; Vorrat und Liste werden genauso abgezogen.
 */
export async function recipeNeeds(
  recipeId: string,
  portions: number,
  householdId: string | null,
  database: KraftwerkDatabase = db,
): Promise<ShoppingNeeds> {
  const recipes = (await database.recipes.toArray()).filter((row) => !row.deletedAt);
  const ingredients = (
    await database.recipe_ingredients.where('recipeId').equals(recipeId).toArray()
  ).filter((row) => !row.deletedAt);
  const foods = (await database.foods.toArray()).filter((row) => !row.deletedAt);
  const shared = householdId
    ? {
        list: await database.shopping_items.where('householdId').equals(householdId).toArray(),
        pantry: await database.pantry_items.where('householdId').equals(householdId).toArray(),
      }
    : { list: [], pantry: [] };

  return buildShoppingNeeds({
    plan: [{ date: '', recipeId, foodId: null, portions, grams: null }],
    recipes,
    ingredients,
    foods: foods.map((food) => ({
      id: food.id,
      name: food.name,
      category: food.category ?? 'other',
      portionGrams: food.portionGrams,
    })),
    pantry: shared.pantry.filter((item) => !item.deletedAt),
    onList: shared.list.filter((item) => !item.deletedAt && !item.checkedAt),
  });
}

/** Die Tage, die „diese Woche" meint: heute und die sechs folgenden. */
export function planWindow(today: string): { from: string; to: string } {
  return { from: today, to: addDays(today, 6) };
}

/**
 * Die gewählten Zeilen auf die Liste setzen — mit der fehlenden Menge in Gramm.
 *
 * Zeilen ohne bekannte Menge kommen ohne Menge auf die Liste, nicht mit 0 g.
 */
export async function addNeedsToList(
  lines: readonly NeedLine[],
  database: KraftwerkDatabase = db,
): Promise<number> {
  const householdId = await requireHousehold(database);
  const onList = new Set(
    (await listShopping(householdId, database))
      .filter((item) => !item.checkedAt)
      .map((item) => itemKey(item.name)),
  );

  // Der Laden, in dem man es zuletzt gekauft hat — auch aus Erledigtem.
  const history = await database.shopping_items.where('householdId').equals(householdId).toArray();
  const liveShops = new Set(
    (await database.shops.where('householdId').equals(householdId).toArray())
      .filter((shop) => !shop.deletedAt)
      .map((shop) => shop.id),
  );

  const changes: RowChange[] = [];
  for (const line of lines) {
    const amount = line.toBuyG !== null && line.toBuyG > 0 ? line.toBuyG : null;
    // Steht ohne Menge schon da und es gibt keine Menge zu ergänzen: nichts doppelt.
    if (amount === null && onList.has(line.key)) continue;
    changes.push({
      table: 'shopping_items',
      row: shoppingRow(
        householdId,
        {
          name: line.name,
          amount,
          unit: amount === null ? null : 'g',
          category: line.category,
          shopId: rememberedShop(line.name, history, liveShops),
        },
        true,
      ),
    });
  }
  await writeRows(changes, database);
  return changes.length;
}

// --- Bald wieder dran (beobachteter Verbrauch, 27.10.2026) ----------------------

/**
 * Ausgeblendete Vorschläge — **je Gerät** in `meta`, nicht im Profil: Das
 * Profil ist lesbar, die Namen aus dem Einkauf sind es nicht (E21). Je
 * Schlüssel steht der Kauftag, an dem ausgeblendet wurde; ein neuer Kauf hebt
 * es auf.
 */
const HIDDEN_SUGGESTIONS_KEY = 'consumption:hidden';

export async function readHiddenSuggestions(
  database: KraftwerkDatabase = db,
): Promise<Record<string, IsoDate>> {
  const entry = await database.meta.get(HIDDEN_SUGGESTIONS_KEY);
  if (!entry) return {};
  try {
    const parsed: unknown = JSON.parse(entry.value);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, IsoDate>) : {};
  } catch {
    return {};
  }
}

export async function hideSuggestion(
  suggestion: ConsumptionSuggestion,
  database: KraftwerkDatabase = db,
): Promise<void> {
  if (!suggestion.pattern.lastBought) return;
  const hidden = await readHiddenSuggestions(database);
  hidden[suggestion.key] = suggestion.pattern.lastBought;
  await database.meta.put({ key: HIDDEN_SUGGESTIONS_KEY, value: JSON.stringify(hidden) });
}

/**
 * Was bald wieder dran ist. Die ganze Geschichte der Liste — auch weich
 * Gelöschtes, denn „Einkauf abschließen“ löscht Abgehaktes weich. Flach
 * gelesen, der Haushalt kommt hinein (Live-Abfrage).
 */
export async function listConsumptionSuggestions(
  householdId: string | null,
  today: IsoDate,
  timeZone: string,
  database: KraftwerkDatabase = db,
): Promise<ConsumptionSuggestion[]> {
  if (!householdId) return [];
  const [rows, hidden] = await Promise.all([
    database.shopping_items.where('householdId').equals(householdId).toArray(),
    readHiddenSuggestions(database),
  ]);
  return consumptionSuggestions(rows, { today, timeZone, hidden });
}

/** „Auf die Liste“: ein ganz normaler Eintrag, im zuletzt benutzten Laden. */
export async function addSuggestionToList(
  suggestion: ConsumptionSuggestion,
  database: KraftwerkDatabase = db,
): Promise<LocalShoppingItem> {
  const householdId = await requireHousehold(database);
  const history = await database.shopping_items.where('householdId').equals(householdId).toArray();
  const liveShops = new Set(
    (await database.shops.where('householdId').equals(householdId).toArray())
      .filter((shop) => !shop.deletedAt)
      .map((shop) => shop.id),
  );
  return addShoppingItem(
    {
      name: suggestion.name,
      category: suggestion.category,
      shopId: rememberedShop(suggestion.name, history, liveShops),
    },
    database,
  );
}

// --- Haushalt ------------------------------------------------------------------

export async function listMembers(
  householdId: string | null,
  database: KraftwerkDatabase = db,
): Promise<LocalHouseholdMember[]> {
  if (!householdId) return [];
  const rows = await database.household_members.where('householdId').equals(householdId).toArray();
  return rows
    .filter((row) => !row.deletedAt)
    .sort((a, b) => a.displayName.localeCompare(b.displayName, 'de'));
}

/**
 * Mitgliedschaft ändern geht nur online — absichtlich. Ein Beitritt, der offline
 * „geklappt" hätte und Stunden später abgelehnt wird, wäre eine Liste, die man
 * für geteilt hält und die es nicht ist.
 */
export function createInviteCode(): Promise<{ code: string; expiresAt: string }> {
  return apiFetch('/households/invites', { method: 'POST' });
}

export function joinHousehold(code: string): Promise<{ householdId: string; movedItems: number }> {
  return apiFetch('/households/join', { method: 'POST', body: JSON.stringify({ code }) });
}

export function leaveHousehold(): Promise<{ householdId: string }> {
  return apiFetch('/households/leave', { method: 'POST' });
}
